import FilledDropdownIcon from "@/assets/4D64FC_dropdown_icon.svg?react";
import CircleWithCheckmarkIcon from "@/assets/C9CAF9_circle_checkmark_icon.svg?react";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import ReusableDropdown from "@/components/Common/forms/ReusableDropdown";
import clsxm from "@/lib/clsxm";
import {PERMISSIONS} from "@/store/constants";
import type {TPermission} from "@/store/types";
import {useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";

const PermissionSelect: FC<{
  value: TPermission | null;
  onChange: (value: string) => void;
  excludedPermissions?: TPermission[];
  topOptions?: Array<{value: string; label: string}>;
  bottomOptions?: Array<{value: string; label: string}>;
  placeholder?: string;
  containerClassName?: string;
  dropdownClassName?: string;
  dropDownIconVariant?: "OUTLINE" | "FILLED";
}> = ({
  value,
  onChange,
  excludedPermissions,
  topOptions = [],
  bottomOptions = [],
  placeholder,
  containerClassName,
  dropdownClassName,
  dropDownIconVariant = "OUTLINE",
}) => {
  const {t} = useTranslation("multi_account");
  const defaultPermissionOptions = useMemo(
    () =>
      PERMISSIONS.filter(
        permission =>
          permission !== "REFLUENCED_COMPANY_ADMIN" &&
          permission !== "REFLUENCED_INFLUENCER_ADMIN" &&
          permission !== "ADMIN" &&
          !excludedPermissions?.includes(permission),
      ).map(permission => ({
        value: permission,
        label: t(`${permission.toLowerCase()}_permissions`),
      })),
    [t],
  );
  const dropdownOptions = useMemo(
    () => [...topOptions, ...defaultPermissionOptions, ...bottomOptions],
    [defaultPermissionOptions, topOptions, bottomOptions],
  );

  return (
    <ReusableDropdown
      shouldUseCssDivide={false}
      containerClassName={clsxm("min-w-[180px] md:max-w-[185px]", containerClassName)}
      dropdownClassName={clsxm("translate-y-12", dropdownClassName)}
      setSelectedOption={option => onChange(option.value)}
      selectedOption={dropdownOptions.find(permission => permission.value === value)}
      options={dropdownOptions}
      renderButton={({selectedOption, isOpen, className, ...rest}) => (
        <button
          {...rest}
          className={clsxm(
            className,
            selectedOption ? "text-neutral-black" : "text-neutral-grey-light",
            "border-primary-light-solid h-auto gap-2 px-4 py-2",
          )}>
          <span className="grow items-center text-left text-xs">
            {!!selectedOption ? selectedOption?.label : placeholder}
          </span>
          {dropDownIconVariant === "OUTLINE" && (
            <RightChevronIcon
              className={clsxm(
                "text-neutral-black size-2 transition-transform duration-250",
                isOpen ? "rotate-[270deg]" : "rotate-90",
              )}
            />
          )}
          {dropDownIconVariant === "FILLED" && (
            <FilledDropdownIcon
              className={clsxm("mr-4 transition-transform duration-250", isOpen ? "rotate-180" : "rotate-0")}
            />
          )}
        </button>
      )}
      renderItem={({option, isHighlighted, isSelected, className, ...rest}) => {
        return (
          <li
            {...rest}
            value={option.value}
            className={clsxm(
              className,
              isHighlighted ? "bg-primary-light-solid" : "hover:bg-neutral-white-dark",
              rest.key === topOptions.length - 1
                ? "border-b-neutral-white-dark border-b"
                : !!bottomOptions.length && rest.key === topOptions.length + defaultPermissionOptions.length
                  ? "border-t-neutral-white-dark border-t"
                  : "",
              "h-auto gap-2 py-3",
            )}>
            {isSelected ? (
              <CircleWithCheckmarkIcon className="size-[14px] shrink-0" />
            ) : (
              <div className="size-[14px] shrink-0" />
            )}
            <span className="grow text-xs leading-none">{option.label}</span>
          </li>
        );
      }}
    />
  );
};

export default PermissionSelect;
